import React, { useCallback, useEffect, useRef, useState } from "react";
import { AGENT_VOICES, AUDIO_BASE, SCRIPT, filesFor, timingFor, words, wordsBy } from "../data/voiceDemo";
import "./VoiceCallDemo.css";

// Sales and Voice Agent page: a simulated inbound sales call (script in data/voiceDemo.js). Each line plays a
// pre-recorded neural voice and the captions follow its word timings; visitors can switch Aivio between a female
// and a male voice. If the audio can't play, the browser's own speech is used; with sound off, the same timings
// pace the captions. While the call runs, the agent workspace fills in from each line's effects.
const FIELDS = [
  { key: "company", label: "Company" }, { key: "contact", label: "Contact" }, { key: "seats", label: "Seats" },
  { key: "current", label: "Current setup" }, { key: "timeline", label: "Timeline" }, { key: "decision", label: "Decision makers" },
  { key: "next", label: "Next step" }, { key: "meeting", label: "Meeting" },
];
const WORD_MS = 330;
const GAP_MS = 380;

const speechAvailable = () => typeof window !== "undefined" && "speechSynthesis" in window && "SpeechSynthesisUtterance" in window;

// Fallback voices: natural/online voices first, then Google's, then whatever the device has.
function pickVoices(voices) {
  const english = voices.filter((voice) => /^en[-_]/i.test(voice.lang));
  const find = (patterns, exclude) => {
    for (const pattern of patterns) {
      const match = english.find((voice) => voice !== exclude && pattern.test(`${voice.name} ${voice.lang}`));
      if (match) return match;
    }
    return english.find((voice) => voice !== exclude) || null;
  };
  const agent = find([/(Ava|Emma|Jenny|Aria|Michelle).*(Natural|Online)/i, /(Natural|Neural)/i, /Google US English/i, /Samantha|Karen/i, /Zira/i]);
  const male = find([/(Andrew|Brian|Guy|Christopher|Eric|Ryan).*(Natural|Online)/i, /Google UK English Male/i, /Daniel|Alex|Fred/i, /David|Mark|Ravi/i], agent);
  const caller = find([/(Neerja|Sonia|Libby|Maisie).*(Natural|Online)/i, /(Natural|Neural)/i, /Google UK English Female/i, /Veena|Moira|Tessa|Serena/i, /Hazel|Susan|Heera/i], agent);
  return { agent, male, caller };
}

const formatTime = (ms) => {
  const seconds = Math.floor(ms / 1000);
  return `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
};

// Female/male switch for Aivio's voice; also used by the home page teaser.
export function VoicePicker({ value, onChange, className = "" }) {
  return (
    <div className={`voice-pick ${className}`} role="radiogroup" aria-label="Aivio’s voice">
      <span>Aivio’s voice</span>
      {AGENT_VOICES.map((voice) => (
        <button type="button" role="radio" aria-checked={value === voice.key} className={value === voice.key ? "is-on" : ""} key={voice.key} onClick={() => onChange(voice.key)}>
          {voice.label}
        </button>
      ))}
    </div>
  );
}

function VoiceCallDemo({ useCase }) {
  const [phase, setPhase] = useState("idle"); // idle | live | ended
  const [line, setLine] = useState(0);
  const [spoken, setSpoken] = useState(0); // words of the current line already spoken
  const [paused, setPaused] = useState(false);
  const [sound, setSound] = useState(true);
  const [elapsed, setElapsed] = useState(0);
  const [approved, setApproved] = useState(false);
  const [audioOk, setAudioOk] = useState(true);
  const [agentVoice, setAgentVoice] = useState("female");
  const voices = useRef({ agent: null, male: null, caller: null });
  const player = useRef(null);
  const resumeLine = useRef(-1);
  const transcriptRef = useRef(null);
  const run = useRef(0);

  useEffect(() => {
    if (!speechAvailable()) return undefined;
    const load = () => { voices.current = pickVoices(window.speechSynthesis.getVoices()); };
    load();
    window.speechSynthesis.addEventListener?.("voiceschanged", load);
    return () => {
      window.speechSynthesis.removeEventListener?.("voiceschanged", load);
      window.speechSynthesis.cancel();
    };
  }, []);

  // Play (or pace) the current line, then move to the next one.
  useEffect(() => {
    if (phase !== "live" || paused) return undefined;
    const id = ++run.current;
    const current = SCRIPT[line];
    const total = words(current.text).length;
    const timing = timingFor(line, agentVoice);
    const timers = [];
    let frame = 0;
    let audio = null;
    const next = () => {
      if (id !== run.current) return;
      setSpoken(total);
      timers.push(window.setTimeout(() => {
        if (id !== run.current) return;
        if (line + 1 < SCRIPT.length) { setLine(line + 1); setSpoken(0); } else setPhase("ended");
      }, GAP_MS));
    };

    if (sound && audioOk && timing && player.current) {
      // Recorded voice through one player (unlocked by the Answer click); Hold resumes mid-sentence. Switching
      // Aivio's voice changes the file, so Aivio's current line starts again in the new voice.
      audio = player.current;
      const src = AUDIO_BASE + timing.file;
      if (resumeLine.current !== line || !audio.src.endsWith(timing.file)) {
        audio.src = src;
        audio.currentTime = 0;
      }
      resumeLine.current = line;
      const follow = () => {
        if (id !== run.current) return;
        setSpoken(wordsBy(timing, audio.currentTime));
        frame = window.requestAnimationFrame(follow);
      };
      audio.onended = next;
      audio.play().then(() => { frame = window.requestAnimationFrame(follow); }).catch((error) => {
        // An interrupted play() is expected when the visitor holds or mutes; anything else means no audio here.
        if (id === run.current && error?.name !== "AbortError") setAudioOk(false);
      });
    } else if (sound && speechAvailable()) {
      const synth = window.speechSynthesis;
      synth.cancel();
      const utterance = new SpeechSynthesisUtterance(current.text);
      const voice = voices.current[current.who === "agent" && agentVoice === "male" ? "male" : current.who];
      if (voice) { utterance.voice = voice; utterance.lang = voice.lang; }
      if (current.who === "caller" && voice === voices.current.agent) utterance.pitch = 1.2;
      let boundaries = false;
      utterance.onboundary = (event) => {
        if (id !== run.current || event.name === "sentence") return;
        boundaries = true;
        setSpoken(words(current.text.slice(0, event.charIndex)).length + 1);
      };
      utterance.onend = next;
      utterance.onerror = next;
      // Voices without word events: estimate progress from time.
      const begin = performance.now();
      const estimate = window.setInterval(() => {
        if (!boundaries && id === run.current) setSpoken(Math.min(total - 1, Math.floor((performance.now() - begin) / WORD_MS) + 1));
      }, 120);
      timers.push({ interval: estimate });
      // Chrome can drop an utterance queued straight after cancel(); a short delay avoids that.
      timers.push(window.setTimeout(() => { if (id === run.current) synth.speak(utterance); }, 80));
      // If a voice never reports the end, move on anyway.
      timers.push(window.setTimeout(next, total * WORD_MS * 2.5 + 2500));
    } else {
      // Captions only, paced like the recording.
      const begin = performance.now();
      const duration = timing ? timing.duration : total * WORD_MS / 1000;
      const tick = window.setInterval(() => {
        if (id !== run.current) return;
        const seconds = (performance.now() - begin) / 1000;
        setSpoken(timing ? wordsBy(timing, seconds) : Math.min(total, Math.floor(seconds * 1000 / WORD_MS) + 1));
        if (seconds >= duration) { window.clearInterval(tick); next(); }
      }, 60);
      timers.push({ interval: tick });
    }

    return () => {
      run.current += 1;
      window.cancelAnimationFrame(frame);
      timers.forEach((timer) => (typeof timer === "object" ? window.clearInterval(timer.interval) : window.clearTimeout(timer)));
      if (audio) { audio.onended = null; audio.pause(); }
      if (speechAvailable()) window.speechSynthesis.cancel();
    };
  }, [phase, line, paused, sound, audioOk, agentVoice]);

  // Stop the audio if the visitor leaves the page.
  useEffect(() => () => player.current?.pause(), []);

  // Warm the cache for a voice once the call has started (and again if the visitor switches voice).
  const started = phase !== "idle";
  useEffect(() => {
    if (started) filesFor(agentVoice).forEach((file) => { fetch(AUDIO_BASE + file).catch(() => {}); });
  }, [started, agentVoice]);

  // Call clock.
  useEffect(() => {
    if (phase !== "live" || paused) return undefined;
    const begin = performance.now() - elapsed;
    const timer = window.setInterval(() => setElapsed(performance.now() - begin), 250);
    return () => window.clearInterval(timer);
  }, [phase, paused]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const box = transcriptRef.current;
    if (box) box.scrollTo({ top: box.scrollHeight, behavior: "smooth" });
  }, [line, phase]);

  const start = useCallback(() => {
    // Start the player inside the click so strict autoplay rules (iOS Safari) allow every later line.
    if (!player.current && typeof Audio !== "undefined") {
      player.current = new Audio();
      player.current.preload = "auto";
    }
    resumeLine.current = -1;
    const first = timingFor(0, agentVoice);
    if (player.current && sound && first) {
      player.current.src = AUDIO_BASE + first.file;
      player.current.play().catch(() => {});
      resumeLine.current = 0; // already playing; the line effect shouldn't reload it
    }
    setLine(0); setSpoken(0); setElapsed(0); setApproved(false); setPaused(false); setPhase("live");
  }, [sound, agentVoice]);
  const hangUp = () => { setPhase("ended"); setPaused(false); };

  // Workspace state: everything from finished lines, plus lookups for the line being spoken.
  const done = phase === "ended" ? SCRIPT.length : line;
  const finished = SCRIPT.slice(0, done);
  const fields = Object.assign({}, ...finished.map((item) => item.fields || {}));
  const intent = finished.find((item) => item.intent)?.intent;
  const lookups = SCRIPT.slice(0, phase === "idle" ? 0 : done + 1).filter((item) => item.lookup).map((item) => item.lookup);
  const handoff = SCRIPT.slice(0, phase === "idle" ? 0 : done + 1).some((item) => item.handoff);
  const captured = FIELDS.filter((field) => fields[field.key]).length;
  const mood = finished.filter((item) => item.mood).pop()?.mood;
  // Objections: raised when the cue word is spoken, handled once the agent's answer finishes.
  const answers = Object.assign({}, ...finished.map((item) => item.resolves || {}));
  const answering = phase === "live" ? Object.keys(SCRIPT[line].resolves || {}) : [];
  const current = phase === "live" ? SCRIPT[line] : null;
  const heard = current?.objection && spoken > words(current.text).findIndex((word) => word.startsWith(current.objection.cue));
  const objections = [...finished, ...(heard ? [current] : [])].filter((item) => item.objection).map((item) => ({
    ...item.objection, answer: answers[item.objection.key], answering: answering.includes(item.objection.key),
  }));
  const handled = objections.filter((item) => item.answer).length;
  // Open objections pull the score down until they are handled.
  const score = Math.round(30 + (captured / FIELDS.length) * 50 + handled * 4 - (objections.length - handled) * 6);
  const speaker = phase === "live" ? SCRIPT[line].who : null;

  return (
    <section className="voice-demo-section" id="try-aivio" style={{ "--case-accent": useCase.accent }}>
      <div className="container">
        <div className="voice-demo-heading">
          <div><div className="eyebrow section-eyebrow">TRY AIVIO · LIVE CALL DEMO</div><h2>Listen to a sales call. <span>Watch the agent work.</span></h2></div>
          <p>Aivio answers the call, qualifies the buyer, handles objections and fills in your CRM while the conversation happens, then hands pricing and the quote to your sales team.</p>
        </div>

        <div className="voice-demo-layout">
          <div className={`voice-call is-${phase}${speaker ? ` speaking-${speaker}` : ""}${paused ? " is-paused" : ""}`}>
            <div className="voice-call-head">
              <span className="voice-avatar is-caller" aria-hidden="true">PS</span>
              <div><strong>Priya Sharma</strong><small>Northwind Logistics · +91 98•• ••0187</small></div>
              <span className="voice-call-state">
                {phase === "idle" ? <><i className="is-ring" />Incoming call</> : phase === "live" ? <><i className="is-live" />{paused ? "On hold" : "Live"} · {formatTime(elapsed)}</> : <><i />Call ended · {formatTime(elapsed)}</>}
              </span>
            </div>

            <div className="voice-stage" aria-hidden="true">
              <div className="voice-orb">
                <span className="voice-orb-ring" /><span className="voice-orb-ring is-2" />
                <span className="voice-orb-core">✦</span>
              </div>
              <div className="voice-wave">{Array.from({ length: 28 }, (_, index) => <i key={index} style={{ "--d": `${(index * 137) % 900}ms`, "--h": `${30 + ((index * 53) % 70)}%` }} />)}</div>
              <div className="voice-who">{phase === "idle" ? "Aivio voice agent · ready" : phase === "ended" ? "Call complete" : speaker === "agent" ? "Aivio is speaking" : "Priya is speaking"}</div>
            </div>
            <VoicePicker value={agentVoice} onChange={setAgentVoice} />


            <div className="voice-transcript" ref={transcriptRef} aria-live="polite">
              {phase === "idle" ? (
                <div className="voice-idle"><p>An inbound sales enquiry is ringing. Answer it to hear Aivio handle the call.</p></div>
              ) : SCRIPT.slice(0, done + (phase === "live" ? 1 : 0)).map((item, index) => {
                const isCurrent = phase === "live" && index === line;
                const list = words(item.text);
                return (
                  <div className={`voice-line is-${item.who}${isCurrent ? " is-current" : ""}`} key={index}>
                    <b>{item.who === "agent" ? "Aivio" : "Priya"}</b>
                    <p>{isCurrent ? list.map((word, i) => <span key={i} className={i < spoken ? "is-said" : ""}>{word} </span>) : item.text}</p>
                  </div>
                );
              })}
            </div>

            <div className="voice-controls">
              {phase === "idle" && <button type="button" className="voice-answer" onClick={start}><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2" /></svg>Answer the call</button>}
              {phase === "live" && (
                <>
                  <button type="button" className="voice-btn" onClick={() => setPaused((value) => !value)} aria-label={paused ? "Resume call" : "Hold call"}>
                    {paused ? <svg viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg> : <svg viewBox="0 0 24 24"><path d="M7 5h4v14H7zM13 5h4v14h-4z" /></svg>}{paused ? "Resume" : "Hold"}
                  </button>
                  <button type="button" className="voice-hangup" onClick={hangUp} aria-label="End call"><svg viewBox="0 0 24 24"><path d="M3 15c5-5 13-5 18 0l-2.5 2.5-3-1.5v-2.5a10 10 0 0 0-7 0V16l-3 1.5z" /></svg></button>
                </>
              )}
              {phase === "ended" && <button type="button" className="voice-btn" onClick={start}><svg viewBox="0 0 24 24" className="is-stroke"><path d="M4 12a8 8 0 1 0 2.4-5.7M4 4v4.5h4.5" /></svg>Play the call again</button>}
              <button type="button" className={`voice-btn is-sound${sound ? "" : " is-muted"}`} onClick={() => setSound((value) => !value)} aria-pressed={!sound} aria-label={sound ? "Mute call audio" : "Unmute call audio"}>
                {sound ? <svg viewBox="0 0 24 24" className="is-stroke"><path d="M4 9v6h4l5 4V5L8 9zM16 9a4 4 0 0 1 0 6M18.5 6.5a8 8 0 0 1 0 11" /></svg> : <svg viewBox="0 0 24 24" className="is-stroke"><path d="M4 9v6h4l5 4V5L8 9zM17 9l4 6M21 9l-4 6" /></svg>}
                {sound ? "Sound on" : "Captions only"}
              </button>
            </div>
          </div>

          <div className="voice-workspace">
            <div className="voice-ws-head"><span className="voice-ws-mark">✦</span><div><strong>Agent workspace</strong><small>What Aivio captures during the call</small></div><span className="voice-ws-score"><b>{phase === "idle" ? "--" : score}</b><small>Lead score</small></span></div>
            <div className="voice-ws-meter" aria-hidden="true"><span style={{ transform: `scaleX(${phase === "idle" ? 0 : score / 100})` }} /></div>

            <div className="voice-ws-block">
              <small>INTENT</small>
              {intent ? <div className="voice-intent">{intent}{mood && <span className={`voice-mood is-${mood.toLowerCase()}`}>Caller: {mood}</span>}</div> : <div className="voice-wait">Listening for the reason for the call…</div>}
            </div>

            <div className="voice-ws-block">
              <small>OBJECTIONS · {handled}/{objections.length} HANDLED</small>
              {objections.length ? (
                <ul className="voice-objections">
                  {objections.map((item) => (
                    <li className={item.answer ? "is-handled" : item.answering ? "is-answering" : "is-open"} key={item.key}>
                      <span className="voice-obj-state">{item.answer ? "✓ Handled" : item.answering ? "Responding…" : "Raised"}</span>
                      <strong>{item.label}</strong>
                      {item.answer && <p>{item.answer}</p>}
                    </li>
                  ))}
                </ul>
              ) : <div className="voice-wait">None raised yet</div>}
            </div>

            <div className="voice-ws-block">
              <small>DISCOVERY · {captured}/{FIELDS.length} CAPTURED <em>Salesforce lead</em></small>
              <dl className="voice-fields">
                {FIELDS.map((field) => (
                  <div className={fields[field.key] ? "is-filled" : ""} key={field.key}>
                    <dt>{field.label}</dt><dd>{fields[field.key] || "—"}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="voice-ws-block">
              <small>APPROVED INFORMATION USED</small>
              {lookups.length ? <div className="voice-lookups">{lookups.map((item) => <span key={item}><i>PDF</i>{item}</span>)}</div> : <div className="voice-wait">No lookups yet</div>}
              {handoff && <div className="voice-rule"><span aria-hidden="true">✓</span>Pricing and the quote are handed to Arjun (Account Executive). Aivio doesn’t commit to prices.</div>}
            </div>

            {phase === "ended" && (
              <div className={`voice-summary${approved ? " is-approved" : ""}`}>
                <small>{approved ? "SYNCED TO SALESFORCE" : "CALL SUMMARY · READY FOR REVIEW"}</small>
                <p>Qualified Enterprise enquiry for 400 seats from Northwind Logistics, replacing their current system in March. Two objections handled: migration effort and a cheaper competitor quote. Quote and comparison due by month-end; meeting with Arjun and the IT director on Thursday at 11:00.</p>
                {approved ? (
                  <div className="voice-summary-done"><span>✓</span>Lead updated, meeting booked and follow-up email sent.</div>
                ) : (
                  <div className="voice-summary-actions">
                    <button type="button" onClick={() => setApproved(true)}>Approve CRM update <span aria-hidden="true">✓</span></button>
                    <span>Salesforce · Outlook · Calendar</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <p className="voice-disclaimer">Simulated call with a fictional company. Voices are generated with neural text-to-speech. In production, Aivio connects to your telephony (for example Twilio Flex) and CRM.</p>
      </div>
    </section>
  );
}

export default VoiceCallDemo;
